import { parsePhoneNumberFromString } from 'libphonenumber-js/min';

export interface CountryOption {
  code: string;
  label: string;
}

const COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT",
  "AU", "AW", "AX", "AZ", "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI",
  "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS", "BT", "BV", "BW", "BY",
  "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
  "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM",
  "DO", "DZ", "EC", "EE", "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK",
  "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF", "GG", "GH", "GI", "GL",
  "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR",
  "IS", "IT", "JE", "JM", "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN",
  "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC", "LI", "LK", "LR", "LS",
  "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
  "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW",
  "MX", "MY", "MZ", "NA", "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP",
  "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG", "PH", "PK", "PL", "PM",
  "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM",
  "SN", "SO", "SR", "SS", "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF",
  "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO", "TR", "TT", "TV", "TW",
  "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW"
] as const;

function normalizeLookupText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function getDisplayNames(locales: string[]): any | null {
  const DisplayNames = (Intl as any).DisplayNames;
  if (!DisplayNames) return null;

  try {
    return new DisplayNames(locales, { type: 'region' });
  } catch {
    return null;
  }
}

function getCountryLabel(code: string, locales: string[]): string {
  const displayNames = getDisplayNames(locales);
  const label = displayNames ? displayNames.of(code) : null;
  return label || code;
}

export function resolveCountryLabel(value: any, language: string = 'es'): string {
  const code = normalizeCountryCode(value);
  if (!code) return value ? String(value) : '';
  return getCountryLabel(code, [language || 'es', 'es', 'en']);
}

export function getCountryOptions(language: string = 'es'): CountryOption[] {
  const locales = [language || 'es', 'es', 'en'];

  return [...COUNTRY_CODES]
    .map((code) => ({
      code,
      label: getCountryLabel(code, locales),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function normalizeCountryCode(value: any): string {
  const text = value ? String(value).trim() : '';
  if (!text) return '';

  const upperCode = text.toUpperCase();
  if ((COUNTRY_CODES as readonly string[]).includes(upperCode)) {
    return upperCode;
  }

  const normalizedValue = normalizeLookupText(text);
  for (const code of COUNTRY_CODES) {
    const labels = [
      getCountryLabel(code, ['es']),
      getCountryLabel(code, ['en']),
      getCountryLabel(code, ['ar']),
    ];

    if (labels.some((label) => normalizeLookupText(label) === normalizedValue)) {
      return code;
    }
  }

  return '';
}

export function normalizePhoneNumber(value: any): string {
  const text = value ? String(value).trim() : '';
  if (!text) return '';

  const compact = text.replace(/[\s().-]/g, '');
  const normalized = compact.startsWith('00') ? `+${compact.slice(2)}` : compact;
  const phoneNumber = parsePhoneNumberFromString(normalized);

  if (!phoneNumber || !phoneNumber.isValid()) {
    return '';
  }

  return phoneNumber.number;
}
